import { useState } from 'react';
import { generateReport, searchReports } from '../../services/agentcore.service';
import { checkFeasibility, parseFeasibilityResult } from '../../services/feasibility.service';

export const useReportGeneration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [feasibilityError, setFeasibilityError] = useState(null);
  const [feasibilitySuccess, setFeasibilitySuccess] = useState(null); // NEW: For feasible ideas

  const generate = async (backend, idea, advancedMode, workflowType = 'sequential', useCache = true, modelId = null, questionnaireData = null) => {
    if (!idea.trim()) {
      setError('Please enter an idea to analyze');
      return null;
    }

    setIsLoading(true);
    setError('');
    setFeasibilityError(null);
    setFeasibilitySuccess(null); // Clear previous success state

    try {
      // Check if user wants to force regeneration (bypassing cache suggestions)
      const forceRegenerate = sessionStorage.getItem('forceRegenerate') === 'true';
      if (forceRegenerate) {
        sessionStorage.removeItem('forceRegenerate');
      }

      // Step 1: Check cache/DB FIRST if enabled AND not force regenerating
      if (useCache && !forceRegenerate) {
        try {
          const cacheResponse = await searchReports(
            idea.trim(),
            advancedMode ? 'advanced' : 'standard',
            backend
          );
          
          // If exact match found, return it immediately
          if (cacheResponse.has_exact_match && cacheResponse.exact_match) {
            setIsLoading(false);
            return {
              report: cacheResponse.exact_match,
              suggestions: null,
              cached: true
            };
          }
          
          // If similar reports found, return suggestions
          if (cacheResponse.similar_reports && cacheResponse.similar_reports.length > 0) {
            setIsLoading(false);
            return {
              report: null,
              suggestions: cacheResponse.similar_reports,
              cached: false
            };
          }
        } catch (cacheErr) {
          // Continue with feasibility and generation even if cache check fails
        }
      }

      // Step 2: Check feasibility (via Lambda - simple and debuggable)
      
      let feasibilityResult;
      try {
        feasibilityResult = await checkFeasibility(
          idea.trim(),
          !!questionnaireData,  // questionnaireMode
          modelId
        );
      } catch (feasibilityErr) {
        // Feasibility check failed (service error, not validation failure)
        console.error('❌ Feasibility check service error:', feasibilityErr);
        setError(`Feasibility check failed: ${feasibilityErr.message}`);
        setIsLoading(false);
        return null;
      }
      
      const parsedFeasibility = parseFeasibilityResult(feasibilityResult);
      
      if (!parsedFeasibility.valid) {
        setFeasibilityError({
          score: parsedFeasibility.score,
          issues: parsedFeasibility.issues,
          suggestions: parsedFeasibility.suggestions,
          recommendation: parsedFeasibility.recommendation
        });
        setIsLoading(false);
        return null;
      }
      
      // Set feasibility success data to show in modal
      setFeasibilitySuccess({
        score: parsedFeasibility.score,
        issues: parsedFeasibility.issues,
        suggestions: parsedFeasibility.suggestions,
        strengths: parsedFeasibility.strengths,
        opportunities: parsedFeasibility.opportunities,
        recommendation: parsedFeasibility.recommendation,
        validation_details: parsedFeasibility.validation_details || {},
        message: parsedFeasibility.message || ''
      });

      // Step 3: Generate new report via AgentCore
      const reportResponse = await generateReport(backend, idea.trim(), advancedMode, workflowType, modelId, questionnaireData);

      // NEW: Job-based generation - no polling
      if (reportResponse.status === 'accepted' && reportResponse.job_id) {
        setIsLoading(false);
        
        // Show notification to user
        return {
          report: null,
          suggestions: null,
          cached: false,
          jobSubmitted: true,
          jobId: reportResponse.job_id,
          message: reportResponse.message || 'Report is being generated. Check the Jobs tab for progress.'
        };
      }

      // LEGACY: Direct response (for backward compatibility)
      if (reportResponse.success) {
        const reportData = reportResponse.report;
        
        // Don't save automatically - let user approve first
        // UI will call saveReport() when user clicks "Save Report" button
        
        return {
          report: reportData,
          suggestions: null,
          cached: false,
          needsApproval: true  // Flag to show Save button in UI
        };
      } else {
        setError(reportResponse.error || 'Failed to generate report');
        return null;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error occurred';
      setError(`Error: ${errorMessage}`);
      console.error('Report generation error:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError('');
    setFeasibilityError(null);
    setFeasibilitySuccess(null);
  };

  return { isLoading, error, feasibilityError, feasibilitySuccess, generate, clearError };
};
